// ─── batch-69 magazijn-cijfers (FO-521 §A + FO-520 §D) ──────────────────────
//
// Het "onderdeel"-filter dat de waarde-export, het waarde-overzicht op
// /voorraad/rapportages, de telsessie-scope en de verschillenlijst delen
// (`VoorraadOnderdeelFilter` in de backoffice), plus de filters en DTO's van de
// voorraadwaarde. "Onderdeel" = hoofdgroep (eerste niveau) en categorie
// (tweede niveau), afgeleid via VOORRAAD_CATEGORIE_HOOFDGROEP: geen nieuw
// categoriemodel (spec 220 A1).

import { z } from 'zod'
import {
  VOORRAAD_CATEGORIE,
  VOORRAAD_CATEGORIE_HOOFDGROEP,
  VOORRAAD_HOOFDGROEP,
  type VoorraadCategorie,
  type VoorraadHoofdgroep,
} from './voorraad'
import { queryBoolean } from './zod-query'

// ─── Onderdeel-filter ────────────────────────────────────────────────────────

/**
 * Een lijst uit een JSON-body (array) of een query-string (herhaalde parameter
 * of komma-gescheiden). Leeg = geen filter.
 */
function lijst<T extends z.ZodTypeAny>(item: T) {
  return z.preprocess((v) => {
    if (v === undefined || v === null || v === '') return undefined
    if (Array.isArray(v)) return v
    if (typeof v === 'string') return v.split(',').map((x) => x.trim()).filter((x) => x.length > 0)
    return v
  }, z.array(item).optional())
}

export const voorraadOnderdeelFilterSchema = z.object({
  hoofdgroepen: lijst(z.enum(VOORRAAD_HOOFDGROEP)),
  categorieen: lijst(z.enum(VOORRAAD_CATEGORIE)),
})
export type VoorraadOnderdeelFilter = z.output<typeof voorraadOnderdeelFilterSchema>

/**
 * De categorieën die een onderdeel-filter selecteert; `null` = geen filter
 * (alles). Per gekozen hoofdgroep: zijn er categorieën binnen die groep gekozen,
 * dan alleen die (bv. Apparatuur -> Telefoons), anders de hele groep. Een
 * gekozen categorie buiten de gekozen hoofdgroepen telt ook mee (vereniging):
 * nooit iets stil laten wegvallen.
 */
export function onderdeelFilterCategorieen(f: {
  hoofdgroepen?: readonly VoorraadHoofdgroep[] | null
  categorieen?: readonly VoorraadCategorie[] | null
}): VoorraadCategorie[] | null {
  const hoofdgroepen = [...new Set(f.hoofdgroepen ?? [])]
  const categorieen = [...new Set(f.categorieen ?? [])]
  if (hoofdgroepen.length === 0 && categorieen.length === 0) return null
  const uit = new Set<VoorraadCategorie>()
  for (const hg of hoofdgroepen) {
    const binnen = categorieen.filter((c) => VOORRAAD_CATEGORIE_HOOFDGROEP[c] === hg)
    if (binnen.length > 0) for (const c of binnen) uit.add(c)
    else for (const c of VOORRAAD_CATEGORIE) if (VOORRAAD_CATEGORIE_HOOFDGROEP[c] === hg) uit.add(c)
  }
  for (const c of categorieen) {
    if (!hoofdgroepen.includes(VOORRAAD_CATEGORIE_HOOFDGROEP[c])) uit.add(c)
  }
  return VOORRAAD_CATEGORIE.filter((c) => uit.has(c))
}

// ─── Voorraadwaarde ──────────────────────────────────────────────────────────

/**
 * Waarderingsgrondslag (spec 221 A2): de huidige artikelprijs, of de prijs van
 * de laatste inkooporderregel (zelfde bron als het besteladvies), met terugval
 * op de artikelprijs. Alleen bulk; unieke exemplaren tellen altijd met hun
 * aanschafbedrag.
 */
export const VOORRAAD_WAARDERINGSGRONDSLAG = ['ARTIKELPRIJS', 'LAATSTE_ORDERPRIJS'] as const
export type VoorraadWaarderingsgrondslag = (typeof VOORRAAD_WAARDERINGSGRONDSLAG)[number]

export const VOORRAAD_WAARDERINGSGRONDSLAG_LABELS: Record<VoorraadWaarderingsgrondslag, string> = {
  ARTIKELPRIJS: 'Artikelprijs',
  LAATSTE_ORDERPRIJS: 'Laatste orderprijs',
}

/**
 * Welke locaties tellen mee: alle locaties van het type MAGAZIJN (default: "wat
 * ligt er in het magazijn"), of alle locaties inclusief servicebussen en
 * kantoor. Een gekozen `locatieId` wint van de scope.
 */
export const VOORRAAD_LOCATIE_SCOPE = ['MAGAZIJN', 'ALLE'] as const
export type VoorraadLocatieScope = (typeof VOORRAAD_LOCATIE_SCOPE)[number]

export const VOORRAAD_LOCATIE_SCOPE_LABELS: Record<VoorraadLocatieScope, string> = {
  MAGAZIJN: 'Alle magazijnen',
  ALLE: 'Alle locaties (ook servicebussen en kantoor)',
}

/** Bron van de prijs op een bulk-regel. */
export type VoorraadWaardePrijsBron = 'ARTIKEL' | 'ORDER'

/**
 * De filters van de voorraadwaarde: één schema voor de export-body
 * (`voorraad-waarde`) en de query van `GET /voorraad/rapportages/waarde-per-onderdeel`.
 */
export const voorraadWaardeFilterSchema = voorraadOnderdeelFilterSchema.extend({
  locatieScope: z.enum(VOORRAAD_LOCATIE_SCOPE).default('MAGAZIJN'),
  locatieId: z.string().min(1).optional(),
  /** Uitgegeven unieke items meetellen ("wat heeft de organisatie in omloop"). */
  inclUitgegeven: queryBoolean().default(false),
  /** Alleen regels met een saldo boven 0 (bulk); default aan. */
  alleenMetSaldo: queryBoolean().default(true),
  grondslag: z.enum(VOORRAAD_WAARDERINGSGRONDSLAG).default('ARTIKELPRIJS'),
})
export type VoorraadWaardeFilters = z.output<typeof voorraadWaardeFilterSchema>

export const VOORRAAD_WAARDE_WEERGAVE = ['ONDERDEEL', 'LOCATIE'] as const
export type VoorraadWaardeWeergave = (typeof VOORRAAD_WAARDE_WEERGAVE)[number]

export const voorraadWaardeQuerySchema = voorraadWaardeFilterSchema.extend({
  weergave: z.enum(VOORRAAD_WAARDE_WEERGAVE).default('ONDERDEEL'),
})
export type VoorraadWaardeQuery = z.output<typeof voorraadWaardeQuerySchema>

/** Eén groep in het waarde-overzicht (hoofdgroep, categorie of locatie). */
export interface VoorraadWaardeGroepDTO {
  sleutel: string
  label: string
  /** Bulk-artikelen (artikel x locatie telt één keer per artikel) met saldo in de groep. */
  aantalArtikelen: number
  /** Stuks: bulk-saldo plus unieke exemplaren. */
  aantalStuks: number
  aantalExemplaren: number
  /** Waarde excl. btw in euro, pas aan het eind op centen afgerond. */
  waarde: number
  /** Aandeel in de totale waarde (0..100, één decimaal). */
  percentage: number
  /** Bulk-artikelen zonder prijs (waarde onbekend, niet in `waarde`). */
  artikelenZonderPrijs: number
  /** Unieke exemplaren zonder aanschafbedrag. */
  exemplarenZonderPrijs: number
  /** Onderliggende categorieën (alleen bij een hoofdgroep in de weergave ONDERDEEL). */
  kinderen: VoorraadWaardeGroepDTO[]
}

export interface VoorraadWaardeOverzichtDTO {
  weergave: VoorraadWaardeWeergave
  grondslag: VoorraadWaarderingsgrondslag
  groepen: VoorraadWaardeGroepDTO[]
  totaal: {
    waarde: number
    bulkWaarde: number
    uniekWaarde: number
    aantalArtikelen: number
    aantalStuks: number
    aantalExemplaren: number
    artikelenZonderPrijs: number
    exemplarenZonderPrijs: number
  }
  /** Moment van berekenen (ISO); de peildatum is altijd "nu". */
  berekendOp: string
}

/**
 * KPI "waarvan zonder prijs" op /voorraad/rapportages: bulk-artikelen met
 * voorraad zonder inkoopprijs en exemplaren zonder aanschafbedrag, over de hele
 * voorraad (dezelfde set als de KPI "Voorraadwaarde").
 */
export interface VoorraadZonderPrijsDTO {
  artikelen: number
  exemplaren: number
}

// ─── Voorraad-instellingen (singleton) ───────────────────────────────────────

/**
 * Lineaire afschrijvingstermijn in maanden per categorie (boekwaarde-kolom van
 * de waarde-export, spec 221 A3). Leeg = geen boekwaarde (beslispunt PO).
 */
export const afschrijvingsTermijnenSchema = z.object({
  termijnen: z.partialRecord(
    z.enum(VOORRAAD_CATEGORIE),
    z.number({ error: 'Vul een aantal maanden in' }).int('Hele maanden').min(1, 'Minimaal 1 maand').max(240, 'Maximaal 240 maanden'),
  ),
})
export type AfschrijvingsTermijnenInput = z.infer<typeof afschrijvingsTermijnenSchema>

export interface VoorraadInstellingenDTO {
  afschrijvingsTermijnen: Partial<Record<VoorraadCategorie, number>>
  /** Mag de gebruiker de termijnen wijzigen (VOORRAAD.canEdit + kostprijsrecht)? */
  magAfschrijvingWijzigen: boolean
}
